import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {Award, BookOpen, CheckCircle2, Heart, Leaf, Play, ArrowRight} from 'lucide-react'

// Mesma foto da Mônica usada nas demais páginas do sistema (SobreMonica.tsx)
const FOTO_MONICA = 'https://static.lumi.new/b2/b2c0441cd263f605e6a1cd0bab42c1dc.webp'

const AULAS_LISTA = [
  {
    numero: 1,
    titulo: 'Será que realmente funciona um óleo essencial?',
  },
  {
    numero: 2,
    titulo: 'O óleo essencial não é apenas um cheirinho',
  },
  {
    numero: 3,
    titulo: 'Lavanda e Tangerina: sono, ansiedade e equilíbrio emocional',
  },
  {
    numero: 4,
    titulo: 'Hortelã-Pimenta e Limão Siciliano: energia, foco e digestão',
  },
  {
    numero: 5,
    titulo: 'Melaleuca e Mix Imunidade: proteção e saúde da pele',
  },
  {
    numero: 6,
    titulo: 'Copaíba, Olíbano e Mix Estabilidade: inflamação, memória e aterramento',
  },
  {
    numero: 7,
    titulo: 'Mix Respiratório, Mix Digestivo e Mix Tensão e Dores: soluções completas para o corpo',
  },
]

function CursoInscricao() {
  const navigate = useNavigate()

  // Preserva o parâmetro indicado_por para repassar à página do formulário
  const [queryString, setQueryString] = useState('')

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const indicado = params.get('indicado_por')?.trim()
    if (indicado) {
      setQueryString(`?indicado_por=${encodeURIComponent(indicado)}`)
    }
    window.scrollTo(0, 0)
  }, [])

  function irParaInscricao() {
    navigate(`/curso/inscricao${queryString}`)
  }

  return (
    <div className="min-h-screen bg-[#F8F4EF]">
      {/* Header */}
      <header className="bg-white border-b border-[#EDE0D4] sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <p className="font-cormorant text-lg sm:text-xl font-semibold text-[#2D6A4F] truncate">
            Diagnóstico Saúde e Emoções
          </p>
        </div>
      </header>

      {/* Hero — badge, título, subtítulo e botão de acesso rápido */}
      <section className="px-4 sm:px-6 pt-12 sm:pt-20 pb-10 sm:pb-14">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#D8F3DC] text-[#2D6A4F] text-xs sm:text-sm font-medium mb-6">
            Curso gratuito com certificado
          </span>
          <h1 className="font-cormorant text-4xl sm:text-6xl font-semibold text-[#2C2C2A] leading-[1.1] mb-5">
            Uma Gota de Equilíbrio
          </h1>
          <p className="text-base sm:text-lg text-[#5F5E5A] leading-relaxed max-w-2xl mx-auto mb-8">
            Aprenda como os óleos essenciais podem transformar sua saúde física e emocional, do zero, no seu ritmo.
          </p>
          <button
            type="button"
            onClick={irParaInscricao}
            className="inline-flex items-center gap-2 bg-[#2D6A4F] text-white text-base sm:text-lg font-medium px-8 py-4 rounded-full hover:bg-[#1f4f3a] transition-all shadow-lg hover:shadow-xl"
          >
            Quero me inscrever gratuitamente
            <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Bloco introdutório */}
      <section className="px-4 sm:px-6 py-10 sm:py-14 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-[#2C2C2A] text-base sm:text-lg leading-relaxed space-y-4">
            <p>
              Os óleos essenciais existem há milênios. Mas pouquíssimas pessoas sabem como usá-los de verdade, de forma segura, personalizada e com resultados reais.
            </p>
            <p className="font-medium text-[#2D6A4F]">
              Este curso foi criado para mudar isso.
            </p>
            <p>
              Em 7 aulas curtas, você vai entender o que são os óleos essenciais, como eles atuam no corpo e nas emoções, e como começar a usá-los no seu dia a dia com segurança e confiança.
            </p>
          </div>
        </div>
      </section>

      {/* O que você vai aprender */}
      <section className="px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <BookOpen className="mx-auto text-[#52B788] mb-3" size={32} />
            <h2 className="font-cormorant text-2xl sm:text-4xl font-semibold text-[#2C2C2A]">
              O que você vai aprender
            </h2>
          </div>
          <div className="space-y-3">
            {AULAS_LISTA.map(aula => (
              <div
                key={aula.numero}
                className="flex items-start gap-4 bg-white rounded-2xl p-4 sm:p-5 border border-[#EDE0D4] shadow-sm"
              >
                <div className="shrink-0 w-9 h-9 rounded-full bg-[#D8F3DC] flex items-center justify-center">
                  <span className="font-cormorant font-semibold text-[#2D6A4F] text-base">
                    {aula.numero}
                  </span>
                </div>
                <div className="flex-1 pt-1">
                  <span className="text-xs uppercase tracking-wider text-[#B08968] font-medium">
                    Aula {aula.numero} —
                  </span>
                  <p className="text-[#2C2C2A] text-sm sm:text-base leading-relaxed mt-0.5">
                    {aula.titulo}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* O que você recebe */}
      <section className="px-4 sm:px-6 py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <Award className="mx-auto text-[#B08968] mb-3" size={32} />
            <h2 className="font-cormorant text-2xl sm:text-4xl font-semibold text-[#2C2C2A]">
              O que você recebe
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: <Play size={22} className="text-[#52B788]" />, texto: '7 aulas em vídeo, no seu ritmo' },
              { icon: <CheckCircle2 size={22} className="text-[#52B788]" />, texto: 'Quiz ao final de cada aula' },
              { icon: <Award size={22} className="text-[#52B788]" />, texto: 'Certificado de Aromaterapia Introdutória' },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-3 bg-[#F8F4EF] rounded-2xl p-5 border border-[#EDE0D4]"
              >
                <div className="shrink-0 mt-0.5">{item.icon}</div>
                <p className="text-[#2C2C2A] text-sm sm:text-base leading-relaxed">{item.texto}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Para quem é */}
      <section className="px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <Heart className="mx-auto text-[#B08968] mb-3" size={32} />
            <h2 className="font-cormorant text-2xl sm:text-4xl font-semibold text-[#2C2C2A]">
              Para quem é
            </h2>
          </div>
          <div className="space-y-3">
            {[
              'Quem nunca usou óleos essenciais e quer começar com segurança',
              'Quem já usa mas quer entender melhor como e por quê funcionam',
              'Quem busca alternativas naturais para cuidar da saúde no dia a dia',
            ].map((item) => (
              <div
                key={item}
                className="flex items-start gap-3 bg-white rounded-2xl p-5 border border-[#EDE0D4] shadow-sm"
              >
                <ArrowRight className="text-[#2D6A4F] shrink-0 mt-0.5" size={20} />
                <p className="text-[#2C2C2A] text-sm sm:text-base leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sobre a criadora — com foto da Mônica (mesmo estilo das demais páginas) */}
      <section className="px-4 sm:px-6 py-12 sm:py-16 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 sm:mb-10">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#D8F3DC] text-[#2D6A4F] text-xs sm:text-sm font-medium mb-3">
              Sobre a criadora
            </span>
          </div>

          <div className="flex flex-col md:flex-row items-center md:items-start gap-6 sm:gap-8 md:gap-10 bg-[#F8F4EF] rounded-3xl border border-[#EDE0D4] shadow-sm p-6 sm:p-10 md:p-12">
            {/* Foto */}
            <div className="shrink-0">
              <div className="w-48 h-48 sm:w-56 sm:h-56 md:w-[280px] md:h-[280px] rounded-3xl overflow-hidden border-4 border-[#D8F3DC] shadow-md bg-white">
                <img
                  src={FOTO_MONICA}
                  alt="Mônica Maria - aromaterapeuta e consultora de bem-estar"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Texto */}
            <div className="flex-1 space-y-4 text-center md:text-left">
              <p className="text-[#2C2C2A] text-sm sm:text-base leading-relaxed">
                Olá! Eu sou a Mônica Maria, aromaterapeuta e consultora de bem-estar. Desde 2019 trabalho com óleos essenciais, transformando a saúde física e emocional de centenas de pessoas. Criei este curso porque acredito que qualquer pessoa pode aprender a cuidar de si mesma de forma mais natural, e uma gota é o suficiente para começar.
              </p>

            </div>
          </div>
        </div>
      </section>

      {/* Botão — ao FINAL de todo o conteúdo */}
      <section className="px-4 sm:px-6 py-14 sm:py-20">
        <div className="max-w-2xl mx-auto text-center">
          <Leaf className="mx-auto text-[#52B788] mb-4" size={30} />
          <p className="font-cormorant text-2xl sm:text-3xl font-semibold text-[#2C2C2A] mb-8 leading-snug">
            Pronto(a) para começar sua jornada com os óleos essenciais?
          </p>
          <button
            type="button"
            onClick={irParaInscricao}
            className="inline-flex items-center gap-2 bg-[#2D6A4F] text-white text-base sm:text-lg font-medium px-8 py-4 rounded-full hover:bg-[#1f4f3a] transition-all shadow-lg hover:shadow-xl"
          >
            Quero me inscrever gratuitamente
            <ArrowRight size={20} />
          </button>
        </div>
      </section>

      <footer className="py-8 text-center text-xs sm:text-sm text-[#5F5E5A]">
        © {new Date().getFullYear()} Diagnóstico Saúde e Emoções · Mônica Maria
      </footer>
    </div>
  )
}

export default CursoInscricao
